// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CreateNewGroupModal from "@bg/App/Components/MyListDetails/CreateNewGroupModal";
import copyLines from "@bg/App/Handlers/Pages/MyListDetails/CopyLines";
import deleteGroup from "@bg/App/Handlers/Pages/MyListDetails/DeleteGroup";
import moveLines from "@bg/App/Handlers/Pages/MyListDetails/MoveLines";
import setGroupWishListLineIsSelected from "@bg/App/Handlers/Pages/MyListDetails/SetGroupSelectedLines";
import updateWishListLines from "@bg/App/Handlers/Pages/MyListDetails/UpdateWishListLines";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getWishListLinesDataView } from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import addLinesToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import setAllWishListLinesIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetAllWishListLinesIsSelected";
import translate from "@insite/client-framework/Translate";
import {
    CartLineCollectionModel,
    CartLineModel,
    WishListLineModel,
    WishListModel,
} from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface GroupActionsProps {
    groups: any;
    listGroups: string[];
    wishListLines?: WishListLineModel[];
    currentGroup: string;
    wishList: WishListModel;
    editable?: boolean;
    setgroups: any;
}

const mapStateToProps = (state: CustomAppState) => ({
    selectedGroupLines: state.commonActions["selectedGroupLines"],
    uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
    isWishListLoading: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter).isLoading,
    isCartLoading: getCurrentCartState(state).isLoading,
    isUpdatingCart: state.context.isUpdatingCart,
});

const mapDispatchToProps = {
    setGroupWishListLineIsSelected,
    updateWishListLines,
    updateWishList,
    addLinesToCart,
    setAllWishListLinesIsSelected,
    moveLines,
    copyLines,
    deleteGroup,
};

type Props = GroupActionsProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface GroupActionsStyles {
    selectSortOption?: SelectPresentationProps;
    deleteGroupLink?: LinkPresentationProps;
    groupAddToCartButton?: ButtonPresentationProps;
    groupAcionsWrapper?: InjectableCss;
    selectAllGroup?: InjectableCss;
    copyToGroup?: InjectableCss;
    moveToGroup?: InjectableCss;
    deleteGroup?: InjectableCss;
    addGroupToCart?: InjectableCss;
}

export const styles: GroupActionsStyles = {
    deleteGroupLink: linkIconStyleGenerator({
        icon: "Delete",
        textSize: `${fontConfig.fontSize.regular}px`,
        iconSize: 16,
        additionalTypographyCss: css`
            opacity: 0.7;
            font-weight: ${fontConfig.weight.semiBold};
        `,
    }),
    groupAddToCartButton: {
        sizeVariant: "small",
        css: css`
            &&&,
            &&&:disabled {
                background: ${getColor("primary")};
                color: ${getColor("common.white")};
                border: 2px solid ${getColor("primary")};
            }
            &&&:hover:not(:disabled) {
                background: ${getColor("common.white")};
                color: ${getColor("primary")};
            }
        `,
    },
    groupAcionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            padding: 15px;
            @media print {
                display: none;
            }
            @media (max-width: 481px) {
                justify-content: space-between;
            }
        `,
    },
    selectAllGroup: {
        css: css`
            margin-right: 30px;
            @media (max-width: 481px) {
                margin-bottom: 20px;
            }
        `,
    },
    copyToGroup: {
        css: css`
            margin-right: 30px;
            &&.disabled svg,
            &&.disabled label {
                opacity: 0.5;
            }
            @media (max-width: 788px) {
                margin-top: 10px;
                margin-right: 30px;
            }
            @media (max-width: 481px) {
                width: 100%;
                margin-bottom: 20px;
            }
        `,
    },
    moveToGroup: {
        css: css`
            display: flex;
            margin-right: 30px;
            &&.disabled svg,
            &&.disabled label {
                opacity: 0.5;
            }
            @media (max-width: 481px) {
                width: 100%;
                margin-right: 0;
                margin-bottom: 20px;
            }
            svg {
                margin-left: 5px;
            }
        `,
    },
    selectSortOption: {
        cssOverrides: {
            formField: css`
                flex-direction: row;
                align-items: center;
                width: 235px;

                label {
                    width: auto;
                    font-weight: ${fontConfig.weight.bold};
                    font-size: ${fontConfig.fontSize.small}px;
                    margin-right: 8px;
                    white-space: nowrap;
                }

                &&&& select:disabled {
                    color: ${getColor("text.main")};
                    opacity: 0.5;
                }

                @media (max-width: 481px) {
                    width: 100%;
                }
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
            `,
            inputSelect: css`
                font-size: ${fontConfig.fontSize.small}px;
                height: 32px;
            `,
        },
        iconProps: {
            css: css`
                &&&&&& {
                    right: 0;
                    top: 0;
                }
            `,
        },
    },
    addGroupToCart: {
        css: css`
            flex-grow: 1;
            display: flex;
            justify-content: flex-end;
            @media (max-width: 1093px) {
                justify-content: flex-start;
                margin-top: 10px;
            }
        `,
    },
    deleteGroup: {
        css: css`
            @media (max-width: 853px) {
                margin-top: 10px;
                margin-right: 30px;
            }
            @media (max-width: 481px) {
                order: 10;
                margin: 0;
                display: flex;
                margin-top: 10px;
                && button span + span {
                    display: none;
                }
            }
            &&.disabled {
                pointer-events: none;
                cursor: not-allowed;
                opacity: 0.5;
            }
        `,
    },
};

const GroupActions: React.FC<Props> = ({
    groups,
    wishListLines,
    currentGroup,
    listGroups,
    updateWishListLines,
    setGroupWishListLineIsSelected,
    selectedGroupLines,
    wishList,
    updateWishList,
    addLinesToCart,
    uncategorizedGroupName,
    editable = true,
    setgroups,
    moveLines,
    copyLines,
    deleteGroup,
    isWishListLoading,
    isCartLoading,
    isUpdatingCart,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [moveToGroup, setMoveToGroup] = useState("Choose a Group");
    const [copyToGroup, setCopyToGroup] = useState("Choose a Group");
    const [isDisabled, setIsDisabled] = useState(false);
    const [isMoveCopyDisabled, setIsMoveCopyDisabled] = useState(false);
    const [groupingModalIsOpen, setGroupingModalIsOpen] = useState(false);
    const [isMove, setIsMove] = useState(false);
    const [deleteGroupModalIsOpen, setDeleteGroupModalIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isMovingLine, setIsMovingLine] = useState(false);
    const [isCopyingLine, setIsCopyingLine] = useState(false);

    useEffect(() => {
        if ((groups && !groups[currentGroup]?.length) || !wishListLines || !wishListLines.length) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
        if (
            !selectedGroupLines ||
            (selectedGroupLines && !selectedGroupLines[currentGroup]) ||
            (selectedGroupLines && selectedGroupLines[currentGroup] && !selectedGroupLines[currentGroup]?.length)
        ) {
            setIsMoveCopyDisabled(true);
        } else {
            setIsMoveCopyDisabled(false);
        }
    }, [wishListLines, selectedGroupLines, groups, currentGroup]);

    useEffect(() => {
        if (selectedGroupLines && selectedGroupLines?.[currentGroup.toString()]) {
            const lineIds: string[] = [];
            groups[currentGroup] && groups[currentGroup].forEach((line: any) => lineIds.push(line.lineId));

            if (
                groups[currentGroup] &&
                groups[currentGroup].every((line: any) => selectedGroupLines[currentGroup].indexOf(line.lineId) > -1)
            ) {
                setIsAllSelected(true);
            } else {
                setIsAllSelected(false);
            }
        }
    }, [currentGroup, groups, selectedGroupLines]);

    const selectAllChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        const wishListLineIds: string[] = [];
        groups[currentGroup].forEach((group: any) => {
            if (wishListLines![group.index]) {
                wishListLineIds.push(wishListLines![group.index].id);
            }
        });
        setGroupWishListLineIsSelected({ wishListLineIds, groupName: currentGroup, isSelectAll: !isAllSelected });
    };

    React.useEffect(() => {
        return () => {
            if (listGroups?.length > 0) {
                listGroups.forEach(group => {
                    setGroupWishListLineIsSelected({
                        wishListLineIds: [],
                        groupName: group,
                        isSelectAll: true,
                        clearAll: true,
                    });
                });
            }
        };
    }, []);

    // move item to another group
    const handleSelectMoveToGroup = (e: React.FormEvent<HTMLSelectElement>) => {
        setIsMove(true);
        const newGroup = e.currentTarget.value;
        setMoveToGroup(newGroup);
        if (newGroup === "Create a New Group") {
            setIsCreatingNewGroup();
        } else {
            handleCopyOrMove(newGroup, true);
        }
    };

    // copy item to anothe group
    const handleSelectCopyToGroup = (e: React.FormEvent<HTMLSelectElement>) => {
        setIsMove(false);
        const newGroup = e.currentTarget.value;
        setCopyToGroup(newGroup);

        if (newGroup === "Create a New Group") {
            setIsCreatingNewGroup();
        } else {
            handleCopyOrMove(newGroup, false);
        }
    };

    // shared functionality for copy and move
    const handleCopyOrMove = (newGroup: string, isMoving?: boolean) => {
        // when choosing create a new group then closing the modal, the newGroup will be empty
        // in that case, set copyTo and moveTo to choose a group option
        if (newGroup !== "Choose a Group" || !newGroup) {
            const ids: string[] = [];
            const linesModel: {
                wishListLineId: string;
                groupLineId: string;
                sortOrder?: number;
            }[] = [];
            groups[currentGroup].forEach((item: any) => {
                const lineId = wishListLines![item.index].id;
                if (
                    selectedGroupLines &&
                    selectedGroupLines?.[currentGroup.toString()] &&
                    selectedGroupLines[currentGroup].indexOf(lineId) > -1
                ) {
                    ids.push(lineId);
                    const line = {
                        wishListLineId: lineId,
                        groupLineId: item.groupLineId,
                        sortOrder: groups[newGroup]
                            ? groups[newGroup].length + 1 + selectedGroupLines[currentGroup].indexOf(lineId)
                            : selectedGroupLines[currentGroup].indexOf(lineId) + 1,
                    };
                    linesModel.push(line);
                }
            });

            if (!linesModel || !linesModel.length) {
                setCopyToGroup("Choose a Group");
                return;
            }

            if (isMoving) {
                setIsMovingLine(true);
                moveLines({
                    wishListId: wishList.id,
                    newGroupName: newGroup,
                    linesModel,
                    reloadWishList: true,
                    onComplete: (result: any) => {
                        setIsMovingLine(false);
                        if (result.successful) {
                            setGroupWishListLineIsSelected({
                                wishListLineIds: ids,
                                groupName: currentGroup,
                            });
                        }
                    },
                    onError: error => {
                        toasterContext.addToast({ body: translate(error), messageType: "danger" });
                        setIsMovingLine(false);
                    },
                });
            } else {
                setIsCopyingLine(true);
                copyLines({
                    wishListId: wishList.id,
                    newGroupName: newGroup,
                    linesModel,
                    reloadWishList: true,
                    onComplete: (result: any) => {
                        if (result.successful) {
                            setIsCopyingLine(false);
                            setGroupWishListLineIsSelected({
                                wishListLineIds: ids,
                                groupName: currentGroup,
                            });
                        }
                    },
                    onError: error => {
                        toasterContext.addToast({ body: translate(error), messageType: "danger" });
                        setIsCopyingLine(false);
                    },
                });
            }
        }

        setCopyToGroup("Choose a Group");
        setMoveToGroup("Choose a Group");
    };

    const setIsCreatingNewGroup = () => {
        setGroupingModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setGroupingModalIsOpen(false);
        setCopyToGroup("Choose a Group");
        setMoveToGroup("Choose a Group");
    };

    // delete group ->
    const deleteGroupFn = () => {
        setIsDeleting(true);
        deleteGroup({
            wishListId: wishList.id,
            groupName: currentGroup,
            reloadWishList: true,
            onComplete: () => {
                setIsDeleting(false);
                setDeleteGroupModalIsOpen(false);
            },
            onError: error => {
                toasterContext.addToast({ body: translate(error), messageType: "danger" });
                setIsDeleting(false);
            },
        });
    };

    const addGroupToCart = () => {
        const cartLines: CartLineModel[] = [];
        if (groups && groups[currentGroup]) {
            groups[currentGroup].forEach((item: any) => {
                const line = {
                    ...wishListLines![item.index],
                    qtyOrdered: item.qtyOrdered > 0 ? item.qtyOrdered : 1,
                    notes: item.ntoes,
                } as any as CartLineModel;
                cartLines.push(line);
            });
            addLinesToCart({
                apiParameter: {
                    cartId: API_URL_CURRENT_FRAGMENT,
                    cartLineCollection: { cartLines } as CartLineCollectionModel,
                },
                onSuccess: onAddToCartSuccess,
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const onAddToCartSuccess = (apiResult?: CartLineCollectionModel) => {
        toasterContext.addToast({
            body: apiResult?.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : translate("Added to Cart"),
            messageType: "success",
        });
    };

    const copyMoveOptions = ["Choose a Group", ...listGroups, "Create a New Group"];

    return (
        <StyledWrapper {...styles.groupAcionsWrapper}>
            <StyledWrapper {...styles.selectAllGroup}>
                <Checkbox
                    checked={isAllSelected}
                    onChange={selectAllChangeHandler}
                    data-test-selector="selectAllItems"
                    disabled={isDisabled || !editable}
                >
                    {translate("Select All Products")}
                </Checkbox>
            </StyledWrapper>
            <StyledWrapper {...styles.moveToGroup} className={isDisabled || isMoveCopyDisabled ? "disabled" : ""}>
                <Select
                    label={translate("Move to")}
                    {...styles.selectSortOption}
                    value={moveToGroup}
                    disabled={isDisabled || isMoveCopyDisabled || !editable}
                    onChange={handleSelectMoveToGroup}
                    data-test-selector="myListDetails_GroupMoveTo"
                >
                    {copyMoveOptions.length > 0 &&
                        copyMoveOptions
                            .filter(i => i.toLowerCase() !== currentGroup.toLowerCase())
                            .map(option => (
                                <option value={option} key={`${option}_${currentGroup}`}>
                                    {option}
                                </option>
                            ))}
                </Select>
                {isMovingLine && <LoadingSpinner size={28} color="secondary" />}
            </StyledWrapper>
            <StyledWrapper {...styles.moveToGroup} className={isDisabled || isMoveCopyDisabled ? "disabled" : ""}>
                <Select
                    label={translate("Copy to")}
                    {...styles.selectSortOption}
                    value={copyToGroup}
                    disabled={isDisabled || isMoveCopyDisabled || !editable}
                    onChange={handleSelectCopyToGroup}
                    data-test-selector="myListDetails_GroupCopyTo"
                >
                    {copyMoveOptions.length > 0 &&
                        copyMoveOptions
                            .filter(i => i.toLowerCase() !== currentGroup.toLowerCase())
                            .map(option => (
                                <option value={option} key={`${option}_${currentGroup}`}>
                                    {option}
                                </option>
                            ))}
                </Select>
                {isCopyingLine && <LoadingSpinner size={28} color="secondary" />}
            </StyledWrapper>
            {currentGroup.toLowerCase() !== uncategorizedGroupName.toLowerCase() && editable && (
                <StyledWrapper {...styles.deleteGroup}>
                    <Link onClick={() => setDeleteGroupModalIsOpen(true)} {...styles.deleteGroupLink}>
                        {translate("Delete Group")}
                    </Link>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.addGroupToCart}>
                <Button
                    {...styles.groupAddToCartButton}
                    disabled={isDisabled || isWishListLoading || isUpdatingCart || isCartLoading}
                    onClick={addGroupToCart}
                >
                    {translate("Add Group To Cart")}
                </Button>
            </StyledWrapper>
            <CreateNewGroupModal
                creatingNewGroup={true}
                groupingModalIsOpen={groupingModalIsOpen}
                closeModalCallback={modalCloseHandler}
                callAfterSuccess={handleCopyOrMove}
                isMovingItem={isMove}
            />
            <TwoButtonModal
                modalIsOpen={deleteGroupModalIsOpen}
                headlineText={translate("Delete Group")}
                messageText={`${translate("Are you sure you want to delete this group?")}`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setDeleteGroupModalIsOpen(false)}
                onSubmit={deleteGroupFn}
                submitTestSelector="submitDeleteListItem"
                showSubmitBtnLoadingOn={isDeleting}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupActions);
